import React, { useState } from 'react';
import { placeService } from '../services/api';
import { MOOD_OPTIONS, PIN_STYLES } from '../constants';

const PinForm = ({ location, onCreated, onClose }) => {
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [description, setDescription] = useState('');
  const [style, setStyle] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const remainingChars = 200 - description.length;

  const toggleMood = (mood) => {
    if (selectedMoods.includes(mood)) {
      setSelectedMoods(selectedMoods.filter((m) => m !== mood));
    } else if (selectedMoods.length < 8) {
      setSelectedMoods([...selectedMoods, mood]);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validate limit (max 5 images)
    if (images.length + files.length > 5) {
      setError('You can only upload up to 5 images.');
      return;
    }

    const validFiles = [];

    files.forEach(file => {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(`File ${file.name} is too large (max 5MB)`);
        return;
      }

      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        setError(`File ${file.name} is not a valid image`);
        return;
      }

      validFiles.push(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    setImages([...images, ...validFiles]);
    // Clear error if valid files were added
    if (validFiles.length > 0) setError(null);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedMoods.length === 0) {
      setError('Please select at least one mood');
      return;
    }

    if (description.trim().length === 0) {
      setError('Please add a description');
      return;
    }

    setLoading(true);
    setUploading(true);
    setError(null);

    try {
      // Create FormData if images are present, otherwise use regular JSON
      let newPlace;

      if (images.length > 0) {
        const formData = new FormData();
        formData.append('lat', location.lat);
        formData.append('lng', location.lng);
        formData.append('description', description.trim());
        formData.append('style', style);

        if (spotifyUrl.trim()) {
          formData.append('spotify_url', spotifyUrl.trim());
        }

        // Append images
        images.forEach((image) => {
          formData.append('images[]', image);
        });

        // Append moods as array
        selectedMoods.forEach((mood) => {
          formData.append('moods[]', mood);
        });

        console.log('Sending place data with images');
        newPlace = await Promise.race([
          placeService.createWithImage(formData),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout after 20s')), 20000)
          )
        ]);
      } else {
        const placeData = {
          lat: location.lat,
          lng: location.lng,
          description: description.trim(),
          moods: selectedMoods,
          style: style,
          spotify_url: spotifyUrl.trim() || null,
        };

        console.log('Sending place data:', placeData);
        newPlace = await Promise.race([
          placeService.create(placeData),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout after 10s')), 10000)
          )
        ]);
      }

      console.log('Place created:', newPlace);
      onCreated(newPlace);
    } catch (err) {
      console.error('Error creating place:', err);
      setError(err.message || 'Failed to create place');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto border border-gray-100 dark:border-gray-700 animate-in">
      <div className="flex justify-between items-center mb-6 -mx-6 -mt-6 md:-mx-8 md:-mt-8 p-4 md:p-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 rounded-t-2xl border-b border-indigo-100 dark:border-gray-700">
        <div>
          <h2 className="text-2xl md:text-3xl font-light text-indigo-900 dark:text-white">Leave a Mark</h2>
          <p className="text-xs md:text-sm text-indigo-700 dark:text-gray-400 mt-1">Share your moment with the world</p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 w-8 h-8 rounded-full flex items-center justify-center transition-all text-2xl"
        >
          ×
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Pin Style Selector */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" id="pin-style-label">
            Choose your pin style
          </label>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2" role="radiogroup" aria-labelledby="pin-style-label">
            {PIN_STYLES.map((pinStyle) => (
              <button
                key={pinStyle.id}
                type="button"
                role="radio"
                aria-checked={style === pinStyle.id}
                aria-label={pinStyle.name}
                onClick={() => setStyle(pinStyle.id)}
                className={`relative p-2 rounded-lg border transition-all transform hover:scale-105 flex flex-col items-center justify-center ${style === pinStyle.id
                  ? `border-transparent bg-gradient-to-br ${pinStyle.color} text-white shadow-md scale-105`
                  : 'border-gray-100 bg-white hover:border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 dark:hover:border-gray-500'
                  }`}
              >
                <div className="text-2xl mb-1">{pinStyle.icon}</div>
                <div className={`text-[10px] font-medium truncate w-full text-center ${style === pinStyle.id ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                  {pinStyle.name}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Image Upload - Multi */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Add photos ({images.length}/5)
          </label>

          <div className="grid grid-cols-3 gap-2 mb-2">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="relative group">
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-lg text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
            ))}

            {images.length < 5 && (
              <div className="relative h-24">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="flex flex-col items-center justify-center w-full h-full border-2 border-dashed border-gray-200 rounded-lg hover:border-indigo-400 cursor-pointer transition-colors bg-gray-50 hover:bg-indigo-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 dark:hover:border-gray-500"
                >
                  <div className="flex flex-col items-center gap-1 text-gray-400">
                    <span className="text-2xl">+</span>
                    <span className="text-[10px]">Add Photo</span>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Spotify Input */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Add a song (Spotify Link)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-green-500 text-lg">🎵</span>
            </div>
            <input
              type="text"
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
              placeholder="https://open.spotify.com/track/..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>

        {/* Moods */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            What mood defines this place? ({selectedMoods.length}/8)
          </label>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {MOOD_OPTIONS.map((mood) => (
              <button
                key={mood}
                type="button"
                aria-pressed={selectedMoods.includes(mood)}
                onClick={() => toggleMood(mood)}
                className={`px-2 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 border ${selectedMoods.includes(mood)
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm border-transparent'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                disabled={
                  selectedMoods.length >= 8 && !selectedMoods.includes(mood)
                }
              >
                {mood}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Describe this place
          </label>
          <div className="relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              maxLength={200}
              placeholder="What makes this place special?"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              rows={3}
            />
            <div className="absolute bottom-3 right-3 text-[10px] text-gray-400 bg-white/80 dark:bg-gray-800/80 px-1 rounded dark:text-gray-400">
              {remainingChars} left
            </div>
          </div>
        </div>

        {/* Location display - Moved to bottom */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 text-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            <span className="font-mono text-gray-500">
              {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
            </span>
          </div>
          <span className="text-[10px] text-gray-400 bg-white dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-100 dark:border-gray-700">
            Coordinates attached
          </span>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-400 text-white font-semibold py-3 rounded-xl transition-all transform hover:scale-[1.02] hover:shadow-lg disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {uploading ? 'Uploading...' : 'Creating...'}
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Leave Mark
            </>
          )}
        </button>
      </form>

      {/* Info */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-start gap-2">
        <svg className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-[10px] md:text-xs text-gray-500 leading-relaxed">
          Keep it kind and clean.
        </p>
      </div>
    </div>
  );
};

export default PinForm;
